import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiX, FiAlertCircle, FiClipboard, FiCheckCircle } from "react-icons/fi";

// Unified Components
import SidebarNav from "../../components/MasterSideBar.jsx";
import TopbarNav from "../../components/TopbarNav";
import ReportCaseForm from "../../components/ReportCaseForm";
import MyCases from "../../components/MyCases";

const PMDiscipline = () => {
    const navigate = useNavigate();
    
    // UI State
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showForm, setShowForm] = useState(false);
    
    // Data State
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserId(decoded.id || decoded.userId || decoded._id);
            } catch (err) {
                console.error("Token decode error:", err);
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    const parsedUser = JSON.parse(storedUser);
                    setUserId(parsedUser.id || parsedUser._id);
                }
            }
        } else {
            navigate("/");
        }
    }, [navigate]);

    const handleFormClose = (success = false) => {
        setShowForm(false);
        if (success) {
            toast.success("Case reported successfully! It is now awaiting review.");
        }
    };

    if (!userId) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-xs font-black uppercase tracking-widest text-gray-400">Authenticating...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" />
            
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); navigate("/"); }}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto">
                    <div className="max-w-[1400px] mx-auto space-y-6">
                        
                        {/* Header & Action */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Disciplinary Management</h1>
                                <p className="text-gray-500 text-sm">Monitor reported incidents and track the progress of ongoing cases.</p>
                            </div>
                            <button
                                onClick={() => setShowForm(true)}
                                className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-orange-700 shadow-lg shadow-orange-100 transition-all active:scale-95"
                            >
                                <FiPlus size={18} />
                                Report New Case
                            </button>
                        </div>

                        {/* Quick Insight Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reported by Me</p>
                                    <p className="text-3xl font-black text-gray-800">Cases</p>
                                </div>
                                <div className="p-4 bg-orange-50 text-orange-600 rounded-xl"><FiClipboard size={24}/></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Under Review</p>
                                    <p className="text-3xl font-black text-blue-600">Active</p>
                                </div>
                                <div className="p-4 bg-blue-50 text-blue-600 rounded-xl"><FiAlertCircle size={24}/></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Resolved</p>
                                    <p className="text-3xl font-black text-green-600">Closed</p>
                                </div>
                                <div className="p-4 bg-green-50 text-green-600 rounded-xl"><FiCheckCircle size={24}/></div>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="p-6 border-b border-gray-50 bg-gray-50/30">
                                <h2 className="text-sm font-black text-gray-800 uppercase tracking-widest">Case History Ledger</h2>
                            </div>
                            <div className="p-2 md:p-6">
                                <MyCases userId={userId} />
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Incident Reporting Modal */}
            {showForm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => handleFormClose()}
                    ></div>
                    
                    <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
                            <div>
                                <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">Report Incident</h2>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Confidential Reporting Portal</p>
                            </div>
                            <button
                                onClick={() => handleFormClose()}
                                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
                            >
                                <FiX size={24} />
                            </button>
                        </div>
                        
                        <div className="p-8 overflow-y-auto custom-scrollbar">
                            <ReportCaseForm
                                reporterId={userId}
                                onClose={() => handleFormClose(true)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PMDiscipline;